/**
 * Institutions API: public apply, institutional-admin dashboard
 * IAM service base URL
 */

import apiClient from './auth';

export interface InstitutionalDashboardData {
  institution: {
    institution_uuid: string;
    name: string;
    short_name?: string;
    slots_allocated: number;
    slots_used: number;
    slots_remaining: number;
    contract_end_date?: string;
    status: string;
  };
  recent_uploads: Array<{
    batch_uuid: string;
    upload_date: string;
    submission_count: number;
    slots_consumed: number;
    status: string;
  }>;
  slot_history: Array<{
    history_uuid: string;
    transaction_type: string;
    slots_changed: number;
    previous_balance: number;
    new_balance: number;
    performed_at: string;
    notes?: string;
  }>;
  pending_slot_request: {
    request_uuid: string;
    requested_slots: number;
    notes?: string;
    requested_at: string;
  } | null;
  warnings: string[];
}

export async function getInstitutionalDashboard(): Promise<InstitutionalDashboardData> {
  const response = await apiClient.get<InstitutionalDashboardData>('/institutional-admin/dashboard');
  return response.data;
}

export interface InstitutionApplicationPayload {
  institution_name: string;
  short_name?: string;
  official_email: string;
  phone_number?: string;
  website?: string;
  physical_address?: string;
  city?: string;
  country?: string;
  contact_person_name: string;
  contact_person_email: string;
  contact_person_phone?: string;
  contact_person_title?: string;
  requested_slots?: number;
  application_letter?: string;
}

export async function submitInstitutionApplication(data: InstitutionApplicationPayload): Promise<{
  message: string;
  application: { institution_uuid: string; institution_name: string; application_status: string; submitted_at: string; reference_number: string };
  next_steps: string;
}> {
  const response = await apiClient.post('/institutions/apply', data);
  return response.data;
}

/** Institutional admin: request more slots (super admin approves later) */
export async function submitSlotRequest(slots_requested: number, notes?: string): Promise<{ message: string; request: { request_uuid: string; requested_slots: number; notes?: string; requested_at: string; status: string } }> {
  const response = await apiClient.post('/institutional-admin/slot-request', { slots_requested, notes });
  return response.data;
}

/** Super admin: list slot requests */
export interface SlotRequestRow {
  request_uuid: string;
  institution_uuid: string;
  institution_name: string;
  short_name?: string;
  requested_slots: number;
  notes?: string;
  status: string;
  requested_at: string;
  reviewed_at?: string;
  slots_granted?: number;
  rejection_reason?: string;
}

export async function getSlotRequests(status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending'): Promise<{ requests: SlotRequestRow[] }> {
  const response = await apiClient.get<{ requests: SlotRequestRow[] }>('/super-admin/slot-requests', { params: { status } });
  return response.data;
}

export async function approveSlotRequest(request_uuid: string, slots_granted?: number): Promise<{ message: string; slots_granted: number; new_total_slots: number }> {
  const response = await apiClient.post(`/super-admin/slot-requests/${request_uuid}/approve`, { slots_granted });
  return response.data;
}

export async function rejectSlotRequest(request_uuid: string, rejection_reason?: string): Promise<{ message: string }> {
  const response = await apiClient.post(`/super-admin/slot-requests/${request_uuid}/reject`, { rejection_reason });
  return response.data;
}
